import { useAuthStore } from '../stores/authStore';

const BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1').replace(/\/$/, '');

interface RequestOptions extends RequestInit {
  params?: Record<string, string>;
  responseType?: 'json' | 'blob';
}

export interface ApiResponse<T = any> {
  data: T;
  status: number;
}

async function request<T = any>(endpoint: string, options: RequestOptions = {}): Promise<ApiResponse<T>> {
  const { params, headers, responseType = 'json', ...customConfig } = options;

  const token = localStorage.getItem('accessToken');

  // Don't set Content-Type for FormData — the browser must set it
  // automatically with the correct multipart/form-data boundary.
  const isFormData = customConfig.body instanceof FormData;

  const defaultHeaders: Record<string, string> = {
    ...(!isFormData && responseType === 'json' ? { 'Content-Type': 'application/json' } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const resolvedHeaders: Record<string, string> = {
    ...defaultHeaders,
    ...(headers as Record<string, string>),
  };

  if (isFormData) {
    // Crucial: remove Content-Type so browser / fetch automatically generates
    // multipart/form-data with the correct boundary parameter.
    delete resolvedHeaders['Content-Type'];
    delete resolvedHeaders['content-type'];
  }

  const config: RequestInit = {
    ...customConfig,
    headers: resolvedHeaders,
  };

  let url = endpoint.startsWith('http') ? endpoint : `${BASE_URL}${endpoint.startsWith('/') ? '' : '/'}${endpoint}`;

  if (params) {
    const searchParams = new URLSearchParams(params);
    url += (url.includes('?') ? '&' : '?') + searchParams.toString();
  }

  let response = await fetch(url, config);

  // Handle 401 and Token Refresh
  if (response.status === 401) {
    try {
      await useAuthStore.getState().refreshAccessToken();
      const newToken = localStorage.getItem('accessToken');
      if (newToken) {
        const updatedHeaders = new Headers(config.headers);
        updatedHeaders.set('Authorization', `Bearer ${newToken}`);
        config.headers = updatedHeaders;
        response = await fetch(url, config);
      }
    } catch (refreshError) {
      console.error('Session expired. Redirecting to login.');
      throw refreshError;
    }
  }

  if (!response.ok) {
    let errorMessage = `HTTP error! status: ${response.status}`;
    let errorData: any = {};
    try {
      errorData = await response.json();
      errorMessage = errorData.error || errorData.message || errorMessage;
    } catch (e) { }
    // Include the full response data so callers can check for specific flags.
    // Throw a real Error (not a plain literal) but keep the `.response` shape
    // that existing callers rely on (err.response?.data / err.response?.status).
    const apiError = new Error(errorMessage) as Error & { response: { data: any; status: number } };
    apiError.response = { data: errorData, status: response.status };
    throw apiError;
  }

  let data: any;
  if (responseType === 'blob') {
    data = await response.blob();
  } else {
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      data = await response.text();
    }
  }

  return { data: data as T, status: response.status };
}

const api = {
  get: <T = any>(url: string, options?: RequestOptions) => request<T>(url, { ...options, method: 'GET' }),
  post: <T = any>(url: string, data?: any, options?: RequestOptions) =>
    request<T>(url, { ...options, method: 'POST', body: data instanceof FormData ? data : JSON.stringify(data) }),
  put: <T = any>(url: string, data?: any, options?: RequestOptions) =>
    request<T>(url, { ...options, method: 'PUT', body: data instanceof FormData ? data : JSON.stringify(data) }),
  patch: <T = any>(url: string, data?: any, options?: RequestOptions) =>
    request<T>(url, { ...options, method: 'PATCH', body: data instanceof FormData ? data : JSON.stringify(data) }),
  delete: <T = any>(url: string, options?: RequestOptions) => request<T>(url, { ...options, method: 'DELETE' }),
};

export default api;
